/**
 * Modal de FILTROS genérico de lista — grupos de pílulas (escolha única) mais,
 * opcionalmente, uma faixa numérica em dinheiro.
 *
 * É a generalização do `QuoteFiltersDialog`, feita quando `/clients` e
 * `/catalog` adotaram o desenho de `/quotes`. As duas decisões que ele carrega
 * de lá — e que não são de layout — continuam valendo:
 *
 * **(a) O modal edita um RASCUNHO, não o filtro ativo.** Escrever direto no
 * estado da tela faria a lista se reorganizar atrás do modal a cada tecla
 * digitada no campo de valor — inclusive nos estados intermediários ("R$ 1,00"
 * a caminho de "R$ 1.000,00"), que recortam quase tudo. O rascunho nasce do
 * filtro ativo toda vez que o modal ABRE (não na montagem, senão ele exibiria a
 * escolha da vez anterior ao ser reaberto), e só o "Aplicar" o promove.
 *
 * **(b) Dinheiro entra por MÁSCARA, nunca por digitação livre (#157).** Não
 * existe parser que resolva a ambiguidade do ponto em pt-BR: "1.500" é mil e
 * quinhentos para quem escreve e é 1,5 para quem vem do teclado numérico. A
 * máscara recebe só os dígitos, empurra da direita em centavos e formata. Campo
 * VAZIO vira `null` — `parseBRLInput('')` é `0`, e `0` é um piso legítimo.
 *
 * O `QuoteFiltersDialog` NÃO foi trocado por este: ele tem o próprio guard de
 * varredura e o próprio teste de página, e reescrevê-lo para ganhar simetria
 * mexeria na tela mais delicada do app sem corrigir nada (a lição do #121 sobre
 * fusão por simetria).
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button3D } from '@/components/ui/button-3d';
import { FieldLabel } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { maskBRLInput, numberToMaskedBRL, parseBRLInput } from '@/lib/currencyInput';
import { FILTER_CHIP, FILTER_CHIP_OFF, FILTER_CHIP_ON } from '@/components/filters/filterChips';
import { EMPTY_LIST_RANGE, type ListValueRange } from '@/lib/listFilters';

export interface FilterChoice {
  value: string;
  label: string;
}

export interface FilterGroup {
  /** Chave do grupo no mapa de valores (ex.: `'period'`, `'stock'`). */
  key: string;
  label: string;
  options: readonly FilterChoice[];
}

export interface ListFiltersValue {
  /** Uma escolha por grupo, pela `key` dele. */
  groups: Record<string, string>;
  range: ListValueRange;
}

interface ListFiltersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Descrição do diálogo — diz o que dá para combinar naquela tela. */
  description: string;
  groups: readonly FilterGroup[];
  /** Rótulo da faixa numérica; ausente = a tela não tem faixa. */
  rangeLabel?: string;
  value: ListFiltersValue;
  onApply: (next: ListFiltersValue) => void;
}

export function ListFiltersDialog({
  open,
  onOpenChange,
  description,
  groups,
  rangeLabel,
  value,
  onApply,
}: ListFiltersDialogProps) {
  const [draft, setDraft] = useState<Record<string, string>>(value.groups);
  const [minText, setMinText] = useState('');
  const [maxText, setMaxText] = useState('');

  useEffect(() => {
    if (!open) return;
    setDraft(value.groups);
    setMinText(value.range.min === null ? '' : numberToMaskedBRL(value.range.min));
    setMaxText(value.range.max === null ? '' : numberToMaskedBRL(value.range.max));
    // `value.groups` é remontado pelo chamador a cada render; depender dele aqui
    // resemearia o rascunho e apagaria o que o usuário está escolhendo. O
    // gatilho é a ABERTURA.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Vazio = SEM limite. Ver (b) no cabeçalho. */
  const toLimit = (text: string): number | null => (text.trim() ? parseBRLInput(text) : null);

  const aplicar = () => {
    onApply({
      groups: draft,
      range: rangeLabel ? { min: toLimit(minText), max: toLimit(maxText) } : EMPTY_LIST_RANGE,
    });
    onOpenChange(false);
  };

  const limpar = () => {
    // "Limpar" devolve cada grupo à PRIMEIRA opção — que é, por convenção das
    // três telas, a opção "todos". Zerar para string vazia deixaria o recorte
    // num valor que nenhum botão representa, e nada ficaria marcado.
    const zerado: Record<string, string> = {};
    for (const group of groups) zerado[group.key] = group.options[0]?.value ?? '';
    setDraft(zerado);
    setMinText('');
    setMaxText('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Filtros</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {groups.map((group) => (
            <div key={group.key} className="space-y-2">
              <FieldLabel>{group.label}</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {group.options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setDraft((prev) => ({ ...prev, [group.key]: option.value }))}
                    className={cn(
                      FILTER_CHIP,
                      draft[group.key] === option.value ? FILTER_CHIP_ON : FILTER_CHIP_OFF,
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {rangeLabel && (
            <div className="space-y-2">
              <FieldLabel>{rangeLabel}</FieldLabel>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <FieldLabel htmlFor="lista-filtro-min">De</FieldLabel>
                  <Input
                    id="lista-filtro-min"
                    inputMode="numeric"
                    placeholder="R$ 0,00"
                    value={minText}
                    onChange={(e) => setMinText(maskBRLInput(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <FieldLabel htmlFor="lista-filtro-max">Até</FieldLabel>
                  <Input
                    id="lista-filtro-max"
                    inputMode="numeric"
                    placeholder="Sem limite"
                    value={maxText}
                    onChange={(e) => setMaxText(maskBRLInput(e.target.value))}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          <Button3D color="gray" size="sm" className="flex-1" onClick={limpar}>
            Limpar
          </Button3D>
          <Button3D size="sm" className="flex-1" onClick={aplicar}>
            Aplicar
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
